import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Circle as CircleIcon
} from '@mui/icons-material';
import axios from '../api/axios';

function ConnectionTypes() {
  const [connectionTypes, setConnectionTypes] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#666666',
    line_width: 2,
    line_style: 'solid',
    direction: 'bidirectional'
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const response = await axios.get('/connection-types/');
      setConnectionTypes(response.data);
    } catch (err) {
      setError('Ошибка загрузки данных: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        name: item.name,
        description: item.description || '',
        color: item.color || '#666666',
        line_width: item.line_width || 2,
        line_style: item.line_style || 'solid',
        direction: item.direction || 'bidirectional'
      });
    } else {
      setEditingItem(null);
      setFormData({
        name: '',
        description: '',
        color: '#666666',
        line_width: 2,
        line_style: 'solid',
        direction: 'bidirectional'
      });
    }
    setOpenDialog(true);
    setError('');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
    setFormData({
      name: '',
      description: '',
      color: '#666666',
      line_width: 2,
      line_style: 'solid',
      direction: 'bidirectional'
    });
    setError('');
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      if (editingItem) {
        await axios.put(`/connection-types/${editingItem.id}`, formData);
      } else {
        await axios.post('/connection-types/', formData);
      }

      handleCloseDialog();
      loadData();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот тип подключения?')) {
      return;
    }

    try {
      await axios.delete(`/connection-types/${id}`);
      loadData();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Типы подключений</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Добавить тип
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Цвет</TableCell>
              <TableCell>Название</TableCell>
              <TableCell>Описание</TableCell>
              <TableCell>Использований</TableCell>
              <TableCell align="right">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {connectionTypes.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <CircleIcon sx={{ color: item.color || '#666666', fontSize: 32 }} />
                </TableCell>
                <TableCell>
                  <Typography variant="body1" fontWeight="bold">
                    {item.name}
                  </Typography>
                </TableCell>
                <TableCell>{item.description || '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={item.connections?.length || 0}
                    size="small"
                    color={item.connections?.length > 0 ? "primary" : "default"}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    color="primary"
                    onClick={() => handleOpenDialog(item)}
                    title="Редактировать"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleDelete(item.id)}
                    title="Удалить"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {connectionTypes.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    Нет типов подключений. Добавьте первый тип!
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Диалог создания/редактирования */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingItem ? 'Редактировать тип подключения' : 'Добавить тип подключения'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Название"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            required
          />

          <TextField
            fullWidth
            label="Описание"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            margin="normal"
            multiline
            rows={2}
          />

          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>Цвет</Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <input
                type="color"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                style={{ width: '60px', height: '40px', border: '1px solid #ccc', borderRadius: '4px' }}
              />
              <TextField
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                size="small"
                sx={{ width: '120px' }}
              />
              <CircleIcon sx={{ color: formData.color, fontSize: 32, ml: 1 }} />
            </Box>
          </Box>

          <FormControl fullWidth margin="normal">
            <InputLabel>Ширина линии</InputLabel>
            <Select
              value={formData.line_width}
              onChange={(e) => setFormData({ ...formData, line_width: e.target.value })}
              label="Ширина линии"
            >
              <MenuItem value={1}>1 px (Тонкая)</MenuItem>
              <MenuItem value={2}>2 px (Средняя)</MenuItem>
              <MenuItem value={3}>3 px (Толстая)</MenuItem>
              <MenuItem value={4}>4 px (Очень толстая)</MenuItem>
              <MenuItem value={5}>5 px</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel>Стиль линии</InputLabel>
            <Select
              value={formData.line_style}
              onChange={(e) => setFormData({ ...formData, line_style: e.target.value })}
              label="Стиль линии"
            >
              <MenuItem value="solid">Сплошная</MenuItem>
              <MenuItem value="dashed">Пунктир</MenuItem>
              <MenuItem value="dotted">Точки</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel>Направление</InputLabel>
            <Select
              value={formData.direction}
              onChange={(e) => setFormData({ ...formData, direction: e.target.value })}
              label="Направление"
            >
              <MenuItem value="bidirectional">Двунаправленное (без стрелки)</MenuItem>
              <MenuItem value="source_to_target">От источника к цели</MenuItem>
              <MenuItem value="target_to_source">От цели к источнику</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Отмена</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !formData.name}
          >
            {loading ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ConnectionTypes;
